import { useEffect, useMemo, useRef, useState, useCallback } from 'react';

/**
 * usePanelRefresh
 *
 * Centralises comments-panel refresh behavior with consistent UX:
 * - Clears local comments data before fetching fresh data
 * - Ensures a minimum spinner display time so loading spinner is visible even on fast refetches
 * - Suppresses EmptyState while refresh is in-flight to avoid flashes/flicker
 * - Completes the refresh only after Apollo query loading has settled (post-refetch)
 *
 * Consumers should use:
 * - `shouldShowLoading(isInitialLoaded, loading)` to decide when to render Loading
 * - `!shouldSuppressEmptyState()` to gate EmptyState rendering
 */

/**
 * Registers/unregisters a refresh handler. Passing undefined should de-register the handler.
 */
type RegisterRefreshFn = (handler?: () => Promise<void>) => void;

/**
 * Hook parameters:
 * - refetch: Apollo refetch function for the panel query
 * - clearCommentsData: local store reset to drop stale/deleted threads
 * - registerTriggerCommentPanelRefresh: wiring to external trigger (content types)
 * - queryLoading: current Apollo loading state for the query
 * - minSpinnerMs: minimum time to show spinner (default 300ms)
 */
type UsePanelRefreshParams = {
	refetch: () => Promise<any>;
	clearCommentsData: () => void;
	registerTriggerCommentPanelRefresh: RegisterRefreshFn;
	queryLoading: boolean;
	minSpinnerMs?: number;
	/** When true, disables refresh/loader behavior (used for VR snapshots) */
	disabled?: boolean;
	/** Opt-in: perform a refresh immediately on mount. Defaults to false. */
	runInitialRefreshOnMount?: boolean;
};

export const usePanelRefresh = ({
	refetch,
	clearCommentsData,
	registerTriggerCommentPanelRefresh,
	queryLoading,
	minSpinnerMs = 300,
	disabled = false,
	runInitialRefreshOnMount = false,
}: UsePanelRefreshParams) => {
	// True while we intentionally clear data and await refetch completion UX-wise
	const [isRefreshing, setIsRefreshing] = useState(false);
	// When true, views should not render EmptyState even if data arrays are empty
	const suppressEmptyRef = useRef<boolean>(false);
	// Internal guard signaling that a refetch has completed and we are waiting for Apollo loading=false
	const [awaitingData, setAwaitingData] = useState(false);

	// Core refresh sequence (shared by mount and external trigger)
	const runRefresh = useCallback(async () => {
		suppressEmptyRef.current = true;
		setIsRefreshing(true);
		clearCommentsData();
		const start = performance.now();
		await refetch();
		const elapsed = performance.now() - start;
		if (elapsed < minSpinnerMs) {
			await new Promise((r) => setTimeout(r, minSpinnerMs - elapsed));
		}
	}, [refetch, clearCommentsData, minSpinnerMs]);

	// Refresh initiated by content types (external trigger)
	useEffect(() => {
		if (disabled) {
			// Ensure no handler is registered when disabled
			registerTriggerCommentPanelRefresh(undefined);
			return () => {
				registerTriggerCommentPanelRefresh(undefined);
			};
		}

		const handler = async () => {
			await runRefresh();
			setAwaitingData(true);
		};

		registerTriggerCommentPanelRefresh(handler);
		return () => {
			registerTriggerCommentPanelRefresh(undefined);
		};
	}, [disabled, runRefresh, registerTriggerCommentPanelRefresh]);

	// Initial refresh on mount to reconcile with external deletes/updates
	useEffect(() => {
		if (disabled || !runInitialRefreshOnMount) {
			return;
		}
		let cancelled = false;
		void (async () => {
			await runRefresh();
			if (!cancelled) {
				setAwaitingData(true);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [disabled, runInitialRefreshOnMount, runRefresh]);

	// Finish refresh only after Apollo loading becomes false post-refetch
	useEffect(() => {
		if (disabled) {
			return;
		}
		if (awaitingData && !queryLoading) {
			setIsRefreshing(false);
			suppressEmptyRef.current = false;
			setAwaitingData(false);
		}
	}, [disabled, awaitingData, queryLoading]);

	/**
	 * Returns whether the panel is currently refreshing (clearing data, refetching, or waiting for data).
	 * Use this to gate EmptyState rendering: `!shouldSuppressEmptyState()`
	 */
	const shouldSuppressEmptyState = useMemo(() => {
		if (disabled) {
			return () => false;
		}
		return () => isRefreshing || suppressEmptyRef.current;
	}, [disabled, isRefreshing]);

	/**
	 * Returns whether the view should render the Loading component.
	 * Supply per-view initial-loaded and the live query loading flag.
	 * This combines panel refresh state with the caller's loading flag.
	 */
	const shouldShowLoading = useMemo(() => {
		if (disabled) {
			return () => false;
		}
		return (_isInitialLoaded: boolean, loadingFlag: boolean) =>
			shouldSuppressEmptyState() || loadingFlag;
	}, [disabled, shouldSuppressEmptyState]);

	return {
		isRefreshing,
		suppressEmptyRef,
		shouldSuppressEmptyState,
		shouldShowLoading,
	} as const;
};
